import { useMemo } from 'react';
import DictionaryMappingService from '@/creditflowresearch/domain/DictionaryMapping';
import buildHeaderMapping from '@/creditflowresearch/application/HeaderMenu/buildHeaderMapping';
import {
  transformSectors,
  transformIndustries,
} from '@/creditflowresearch/application/Dictionaries/serverSide/legacyMappers';
import type { IProduct } from '@/creditflowresearch/domain/vo/Product';
import type { IRegion } from '@/creditflowresearch/domain/vo/Region';
import type { ISector } from '@/creditflowresearch/domain/vo/ISector';
import type { IDictionaryIndustry } from '@/creditflowresearch/domain/vo/IDictionary';
import type { IHeaderMenuItems } from '@/creditflowresearch/domain/vo/IHeaderMenuItems';

export interface IUseHeaderMenuProps {
  products: IProduct[],
  regions: IRegion[],
  sectors: ISector[],
  industries: IDictionaryIndustry[],
  headerMenuItems: IHeaderMenuItems | null,
}

const useHeaderMenu = ({ products, regions, sectors, industries, headerMenuItems }: IUseHeaderMenuProps) => {
  const mappedSectorsInput = useMemo(() => transformSectors(sectors), [sectors]);
  const mappedIndustriesInput = useMemo(() => transformIndustries(industries), [industries]);

  return useMemo(() => {
    if (!headerMenuItems) {
      return {
        isHeaderInitialized: false,
        mapping: {},
        defaultActiveMapping: {},
        defaultActiveRegion: {},
        defaultActiveProduct: {},
        sectorsMenu: [],
        industriesMenu: [],
        mappedSectors: {},
        mappedIndustries: {},
        products,
        regions,
      };
    }

    const mappings = buildHeaderMapping(products, regions, headerMenuItems);

    return {
      isHeaderInitialized: true,
      mapping: mappings.mappedData ?? {},
      defaultActiveMapping: mappings.activeMapping ?? {},
      defaultActiveRegion: mappings.defaultRegion ?? {},
      defaultActiveProduct: mappings.defaultProduct ?? {},
      sectorsMenu: DictionaryMappingService.prepareSectorsMenu(mappedSectorsInput),
      industriesMenu: DictionaryMappingService.prepareIndustriesMenu(mappedIndustriesInput),
      mappedSectors: DictionaryMappingService.getMappedSectors(mappedSectorsInput),
      mappedIndustries: DictionaryMappingService.getMappedIndustries(mappedIndustriesInput),
      products,
      regions,
    };
  }, [products, regions, headerMenuItems, mappedSectorsInput, mappedIndustriesInput]);
};

export default useHeaderMenu;
